import { useCurrentUser, type CurrentUser } from "./use-current-user";

/**
 * Entitlement keys of the price list (ADR-006, EP-15), mirroring the constants on the backend's
 * `Entitlements` interface. Spelled out here rather than derived from the generated schema for the
 * same reason as {@link permissions}: OpenAPI describes the map as `Record<string, Entitlement>`, so
 * a typo would only show up as a screen that quietly gates itself.
 */
export const features = [
  "users.seats",
  "inventory.locations",
  "inventory.forecast",
  "roastprofile.reference",
  "cupping.blind",
  "sales.price_levels",
  "traceability.full",
  "ecommerce.sync",
  "ecommerce.connectors",
  "planning.schedule",
  "reporting.margin",
  "reporting.weekly_digest",
  "labels.designer",
  "traceability.public",
  "subscriptions.b2c",
  "b2b.portal",
  "b2b.standing_orders",
  "api.public",
  "receivables.basic",
  "receivables.bank_import",
  "receivables.dunning",
  "compliance.pl",
  "compliance.eudr",
  "compliance.oss",
  "shipping.labels",
  "shipping.tracking",
  "crm.wholesale",
  "crm.equipment",
  "pos.cafe",
  "analytics.forecast",
  "analytics.simulator",
  "sustainability.footprint",
  "services.contract",
  // EP-38. Six keys that gate a whole section or one step of it; whether they are *visible* is a
  // separate question the release list answers, and `reason` on the allowance is how a screen knows.
  "integration.section",
  "shipping.couriers",
  "ecommerce.marketplaces_pl",
  "integration.ksef",
  "purchasing.market_feed",
  "pos.import",
  // EP-51. Promotions and discount codes on our own price list: in every plan, withheld until payments
  // exist (EP-21). A key for the release gate, not something a plan sells.
  "billing.promotions",
  // EP-54. The checkout for our plans and services: in every plan, withheld while it is a mock-up for
  // acceptance; the operator unhides it for one roastery with an early access override.
  "billing.checkout",
] as const;

export type Feature = (typeof features)[number];

/** What the plan allows for one feature: whether it is included, and how many are allowed. */
export interface Allowance {
  enabled: boolean;
  /** `null` means the plan does not cap it. */
  limit: number | null;
  /**
   * Where the answer came from (EP-38). `UNRELEASED` means this installation withholds the feature
   * from everybody, so a screen renders *nothing* — not an offer to upgrade, which would advertise
   * something nobody can buy. `EARLY_ACCESS` is the same feature unhidden for this roastery alone.
   */
  reason: AllowanceReason;
}

export type AllowanceReason = "PLAN" | "OVERRIDE" | "UNRELEASED" | "EARLY_ACCESS";

/**
 * What the roastery's plan allows, as the backend resolved it. An unknown key reads as allowed, which
 * is the same answer the backend gives (EP-15, decision 4): an entitlement nobody has defined is not
 * a paid feature, and a missing data row must not take a working screen away.
 */
export function allowanceOf(user: CurrentUser | undefined, feature: Feature): Allowance {
  const entitlement = user?.entitlements?.[feature];
  if (!entitlement) {
    return { enabled: true, limit: null, reason: "PLAN" };
  }
  return {
    enabled: entitlement.enabled !== false,
    limit: entitlement.limit ?? null,
    reason: (entitlement.reason as AllowanceReason | undefined) ?? "PLAN",
  };
}

/**
 * Whether the feature is part of the roastery's plan. A plain boolean, and false while
 * `GET /api/v1/me` is in flight — the same rule as `useCan`, and for a reason found in verification:
 * opening the gate while the answer loads let the screen behind it mount and fire its query, so a
 * roastery without the feature got the offer *and* a red 402 in the console on every cold load. The
 * gate stays shut until the plan is known; {@link UpsellGate} renders its `pending` in that moment,
 * so nobody is shown an offer for something they have already paid for.
 */
export function useEntitlement(feature: Feature): boolean {
  const { user, isLoading } = useCurrentUser();
  return !isLoading && allowanceOf(user, feature).enabled;
}

/**
 * The full allowance, for the screens that show the number rather than just the gate — "3 of 3 price
 * levels used". `isLoading` is exposed so those screens can wait rather than render a wrong limit.
 */
export function useAllowance(feature: Feature): Allowance & { isLoading: boolean } {
  const { user, isLoading } = useCurrentUser();
  return { ...allowanceOf(user, feature), isLoading };
}
